import {
  ActionIcon, Badge, Button, Card, Checkbox, Group, Modal, Select, SimpleGrid, Stack, Switch, Table, Text, TextInput, Title,
} from "@mantine/core";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { type Schemas, api, call, hasPermission } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { ErrorAlert, Loading, notifyError, notifyOk } from "../../components/common";

type Pattern = Schemas["ShiftPatternOut"];
type ShiftIn = Schemas["ShiftIn"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const hhmm = (t: string) => t.slice(0, 5);
export const formatDays = (days: number[]) =>
  days.length === 7 ? "every day" : days.map((d) => DAYS[d - 1]).join(", ");
const hours = (seconds: number) => `${(seconds / 3600).toFixed(seconds % 3600 ? 2 : 0)} h`;

/** "12:00-12:30, 18:00-18:15" <-> breaks */
export function parseBreaks(text: string): Schemas["BreakIn"][] {
  return text.split(",").map((s) => s.trim()).filter(Boolean).map((s, i) => {
    const [start_time, end_time] = s.split("-").map((x) => x.trim());
    return { name: `Break ${i + 1}`, start_time, end_time };
  });
}

export function ShiftsPage() {
  const { me } = useAuth();
  const factories = useQuery({ queryKey: ["factories"], queryFn: () => call(api.GET("/api/v1/factories")) });
  const [factoryId, setFactoryId] = useState<string | null>(null);
  useEffect(() => {
    if (!factoryId && factories.data?.length) setFactoryId(factories.data[0].id);
  }, [factories.data, factoryId]);
  if (factories.isLoading) return <Loading />;
  if (factories.error) return <ErrorAlert error={factories.error} />;
  const factory = factories.data!.find((f) => f.id === factoryId);
  return (
    <Stack>
      <Group justify="space-between" align="end">
        <div>
          <Title order={2}>Shifts & calendar</Title>
          <Text size="sm" c="dimmed">Working time is the basis for planned production time, OEE and shift reports.</Text>
        </div>
        <Select label="Factory" data={factories.data!.map((f) => ({ value: f.id, label: f.name }))} value={factoryId}
          onChange={setFactoryId} />
      </Group>
      {factory && <FactoryCalendar key={factory.id} factory={factory} canManage={hasPermission(me, "factory:manage")} />}
    </Stack>
  );
}

function FactoryCalendar({ factory, canManage }: { factory: Schemas["FactoryOut"]; canManage: boolean }) {
  const qc = useQueryClient();
  const path = { factory_id: factory.id };
  const [editing, setEditing] = useState<Pattern | "new" | null>(null);
  const patterns = useQuery({
    queryKey: ["shift-patterns", factory.id],
    queryFn: () => call(api.GET("/api/v1/factories/{factory_id}/shift-patterns", { params: { path } })),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["shift-patterns", factory.id] });
    void qc.invalidateQueries({ queryKey: ["shift-calendar", factory.id] });
  };
  const makeDefault = useMutation({
    mutationFn: (id: string) => call(api.PATCH("/api/v1/shift-patterns/{pattern_id}", {
      params: { path: { pattern_id: id } }, body: { is_default: true },
    })),
    onSuccess: refresh,
    onError: notifyError,
  });
  return (
    <Stack>
      <Group justify="space-between">
        <Title order={4}>Shift patterns</Title>
        {canManage && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setEditing("new")}>New pattern</Button>}
      </Group>
      {patterns.data?.length === 0 && (
        <Text size="sm" c="dimmed">No shift pattern yet. Without one, no planned time exists and OEE cannot be calculated.</Text>
      )}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {(patterns.data ?? []).map((p) => (
          <Card key={p.id} withBorder>
            <Group justify="space-between" mb="xs">
              <Group gap="xs">
                <Text fw={600}>{p.name}</Text>
                {p.is_default && <Badge size="sm">factory default</Badge>}
              </Group>
              {canManage && (
                <Group gap={4}>
                  {!p.is_default && <Button size="compact-xs" variant="default" onClick={() => makeDefault.mutate(p.id)}>Make default</Button>}
                  <ActionIcon variant="subtle" aria-label={`Edit ${p.name}`} onClick={() => setEditing(p)}><IconPencil size={16} /></ActionIcon>
                </Group>
              )}
            </Group>
            <Table>
              <Table.Tbody>
                {p.shifts.map((s) => (
                  <Table.Tr key={s.id}>
                    <Table.Td>{s.name}</Table.Td>
                    <Table.Td>{hhmm(s.start_time)}–{hhmm(s.end_time)}</Table.Td>
                    <Table.Td><Text size="xs">{formatDays(s.weekdays)}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{s.breaks.map((b) => `${hhmm(b.start_time)}–${hhmm(b.end_time)}`).join(", ") || "no breaks"}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        ))}
      </SimpleGrid>
      <Exceptions factory={factory} patterns={patterns.data ?? []} canManage={canManage} />
      <WeekPreview factory={factory} />
      <Modal opened={editing !== null} onClose={() => setEditing(null)} size="xl"
        title={editing === "new" ? "New shift pattern" : `Edit ${editing?.name ?? ""}`}>
        {editing && <PatternForm factoryId={factory.id} pattern={editing === "new" ? null : editing}
          onDone={() => { setEditing(null); refresh(); }} />}
      </Modal>
    </Stack>
  );
}

function PatternForm({ factoryId, pattern, onDone }: { factoryId: string; pattern: Pattern | null; onDone: () => void }) {
  const templates = useQuery({ queryKey: ["shift-templates"], queryFn: () => call(api.GET("/api/v1/catalog/shift-templates")) });
  const [name, setName] = useState(pattern?.name ?? "");
  const [isDefault, setIsDefault] = useState(pattern?.is_default ?? false);
  const [template, setTemplate] = useState<string | null>(pattern ? null : "two_shifts");
  const [rows, setRows] = useState<(ShiftIn & { breaksText: string })[]>(
    (pattern?.shifts ?? []).map((s) => ({
      name: s.name, start_time: hhmm(s.start_time), end_time: hhmm(s.end_time), weekdays: s.weekdays,
      breaksText: s.breaks.map((b) => `${hhmm(b.start_time)}-${hhmm(b.end_time)}`).join(", "),
    })),
  );
  const useTemplate = (code: string | null) => {
    setTemplate(code);
    const t = templates.data?.find((x) => x.code === code);
    if (t) {
      if (!name) setName(t.name);
      setRows(t.shifts.map((s) => ({ ...s, breaksText: (s.breaks ?? []).map((b) => `${hhmm(b.start_time)}-${hhmm(b.end_time)}`).join(", ") })));
    }
  };
  const save = useMutation({
    mutationFn: () => {
      const shifts = rows.map(({ breaksText, ...s }) => ({ ...s, breaks: parseBreaks(breaksText) }));
      return pattern
        ? call(api.PATCH("/api/v1/shift-patterns/{pattern_id}", {
            params: { path: { pattern_id: pattern.id } }, body: { name, shifts, ...(isDefault ? { is_default: true } : {}) },
          }))
        : call(api.POST("/api/v1/factories/{factory_id}/shift-patterns", {
            params: { path: { factory_id: factoryId } }, body: { name, is_default: isDefault, shifts },
          }));
    },
    onSuccess: () => { notifyOk("Shift pattern saved"); onDone(); },
    onError: notifyError,
  });
  const setRow = (i: number, patch: Partial<(typeof rows)[number]>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <Stack>
      {!pattern && (
        <Select label="Start from template" clearable data={(templates.data ?? []).map((t) => ({ value: t.code, label: t.name }))}
          value={template} onChange={useTemplate} />
      )}
      <Group grow align="end">
        <TextInput label="Pattern name" required value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <Switch label="Factory default" checked={isDefault} onChange={(e) => setIsDefault(e.currentTarget.checked)} />
      </Group>
      <Table>
        <Table.Thead>
          <Table.Tr><Table.Th>Shift</Table.Th><Table.Th>Start</Table.Th><Table.Th>End</Table.Th><Table.Th>Days</Table.Th><Table.Th>Breaks (hh:mm-hh:mm, …)</Table.Th><Table.Th /></Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r, i) => (
            <Table.Tr key={i}>
              <Table.Td><TextInput size="xs" w={90} value={r.name} onChange={(e) => setRow(i, { name: e.currentTarget.value })} /></Table.Td>
              <Table.Td><TextInput size="xs" type="time" value={r.start_time} onChange={(e) => setRow(i, { start_time: e.currentTarget.value })} /></Table.Td>
              <Table.Td><TextInput size="xs" type="time" value={r.end_time} onChange={(e) => setRow(i, { end_time: e.currentTarget.value })} /></Table.Td>
              <Table.Td>
                <Checkbox.Group value={r.weekdays.map(String)} onChange={(v) => setRow(i, { weekdays: v.map(Number).sort() })}>
                  <Group gap={4}>{DAYS.map((d, j) => <Checkbox key={d} size="xs" value={String(j + 1)} label={d} />)}</Group>
                </Checkbox.Group>
              </Table.Td>
              <Table.Td><TextInput size="xs" value={r.breaksText} onChange={(e) => setRow(i, { breaksText: e.currentTarget.value })} /></Table.Td>
              <Table.Td><ActionIcon variant="subtle" color="red" aria-label="Remove shift" onClick={() => setRows(rows.filter((_, j) => j !== i))}><IconTrash size={16} /></ActionIcon></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Group justify="space-between">
        <Button size="xs" variant="default" leftSection={<IconPlus size={14} />}
          onClick={() => setRows([...rows, { name: `Shift ${rows.length + 1}`, start_time: "06:00", end_time: "14:00", weekdays: [1, 2, 3, 4, 5], breaksText: "" }])}>
          Add shift
        </Button>
        <Text size="xs" c="dimmed">An end time before the start time means the shift ends the next day.</Text>
      </Group>
      <Button disabled={!name || rows.length === 0} loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
    </Stack>
  );
}

function Exceptions({ factory, patterns, canManage }: { factory: Schemas["FactoryOut"]; patterns: Pattern[]; canManage: boolean }) {
  const qc = useQueryClient();
  const path = { factory_id: factory.id };
  const [form, setForm] = useState({ day: "", kind: "non_working" as "non_working" | "extra_working", shift_id: "", name: "" });
  const list = useQuery({
    queryKey: ["calendar-exceptions", factory.id],
    queryFn: () => call(api.GET("/api/v1/factories/{factory_id}/calendar-exceptions", { params: { path } })),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["calendar-exceptions", factory.id] });
    void qc.invalidateQueries({ queryKey: ["shift-calendar", factory.id] });
  };
  const add = useMutation({
    mutationFn: () => call(api.POST("/api/v1/factories/{factory_id}/calendar-exceptions", {
      params: { path }, body: { ...form, shift_id: form.shift_id || null },
    })),
    onSuccess: () => { setForm({ ...form, day: "", name: "" }); refresh(); },
    onError: notifyError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => call(api.DELETE("/api/v1/calendar-exceptions/{exception_id}", { params: { path: { exception_id: id } } })),
    onSuccess: refresh,
    onError: notifyError,
  });
  const shifts = patterns.flatMap((p) => p.shifts.map((s) => ({ value: s.id, label: `${p.name}: ${s.name}` })));
  const shiftName = (id: string | null | undefined) => shifts.find((s) => s.value === id)?.label ?? "all shifts";
  return (
    <Card withBorder>
      <Title order={5} mb="xs">Holidays, shutdowns and extra shifts</Title>
      <Table>
        <Table.Tbody>
          {(list.data ?? []).map((e) => (
            <Table.Tr key={e.id}>
              <Table.Td>{e.day}</Table.Td>
              <Table.Td><Badge variant="light" color={e.kind === "non_working" ? "gray" : "teal"}>{e.kind === "non_working" ? "non-working" : "extra working"}</Badge></Table.Td>
              <Table.Td>{shiftName(e.shift_id)}</Table.Td>
              <Table.Td>{e.name}</Table.Td>
              <Table.Td>{canManage && <ActionIcon variant="subtle" color="red" aria-label={`Delete ${e.name}`} onClick={() => remove.mutate(e.id)}><IconTrash size={16} /></ActionIcon>}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {list.data?.length === 0 && <Text size="sm" c="dimmed">None.</Text>}
      {canManage && (
        <Group align="end" mt="xs">
          <TextInput size="xs" type="date" label="Date" value={form.day} onChange={(e) => setForm({ ...form, day: e.currentTarget.value })} />
          <Select size="xs" label="Type" w={150} value={form.kind}
            data={[{ value: "non_working", label: "Non-working" }, { value: "extra_working", label: "Extra working" }]}
            onChange={(v) => setForm({ ...form, kind: (v ?? "non_working") as typeof form.kind })} />
          <Select size="xs" label="Shift" w={200} clearable={form.kind === "non_working"}
            placeholder={form.kind === "non_working" ? "All shifts" : "Choose shift"} data={shifts}
            value={form.shift_id || null} onChange={(v) => setForm({ ...form, shift_id: v ?? "" })} />
          <TextInput size="xs" label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
          <Button size="xs" loading={add.isPending} disabled={!form.day || !form.name || (form.kind === "extra_working" && !form.shift_id)}
            onClick={() => add.mutate()}>Add</Button>
        </Group>
      )}
    </Card>
  );
}

function WeekPreview({ factory }: { factory: Schemas["FactoryOut"] }) {
  const cal = useQuery({
    queryKey: ["shift-calendar", factory.id],
    queryFn: () => call(api.GET("/api/v1/factories/{factory_id}/shift-calendar", { params: { path: { factory_id: factory.id } } })),
  });
  const fmt = (iso: string) => new Date(iso).toLocaleString(undefined, { timeZone: factory.timezone, weekday: "short", hour: "2-digit", minute: "2-digit" });
  const total = (cal.data?.shifts ?? []).reduce((s, i) => s + i.working_seconds, 0);
  return (
    <Card withBorder>
      <Group justify="space-between" mb="xs">
        <Title order={5}>This week ({factory.timezone})</Title>
        <Text size="sm">Working time: <b>{hours(total)}</b></Text>
      </Group>
      <Table>
        <Table.Tbody>
          {(cal.data?.shifts ?? []).map((s) => (
            <Table.Tr key={`${s.shift_id}-${s.production_date}`}>
              <Table.Td>{s.production_date}</Table.Td>
              <Table.Td>{s.name} {s.extra && <Badge size="xs" color="teal">extra</Badge>}</Table.Td>
              <Table.Td>{fmt(s.start)} – {fmt(s.end)}</Table.Td>
              <Table.Td>{hours(s.working_seconds)} working ({hours(s.break_seconds)} breaks)</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {cal.data?.shifts.length === 0 && <Text size="sm" c="dimmed">No working time scheduled this week.</Text>}
    </Card>
  );
}
